// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
} from '~/helpers';

export const resolvers = {
  Query: {
    selfConsumptionRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?SelfConsumptionRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        MerchantName: 'merchantName',
        OrderID: 'orderId',
        PersonalReward: 'consumptionReward',
        PersonalRewardUsdAmt: 'consumptionRewardUsd',
        TransferToMegopoly: 'transferToMegopoly',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformId: 'PlatformId',
          merchantName: 'MerchantName',
          orderId: 'OrderID',
          consumptionReward: ['PersonalRewardFrom', 'PersonalRewardTo'],
          consumptionRewardUsd: [
            'PersonalRewardUsdAmtFrom',
            'PersonalRewardUsdAmtTo',
          ],
          transferToMegopoly: 'TransferToMegopoly',
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get self consumption list
      const {
        data: { MyTaskRewardList, PageCount = 1, TotalTaskReward },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetMyTaskRewardTrxList',
        { params },
      );

      const items: SelfConsumptionRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            consumptionReward: parseFloat,
            consumptionRewardUsd: parseFloat,
          }),
          R.assoc('__typename', 'SelfConsumptionRewardHistoryItem'),
        ),
        MyTaskRewardList,
      );

      const selfConsumptionRewardHistory = {
        data: items,
        pages: PageCount,
        totalTaskReward: parseFloat(TotalTaskReward),
        __typename: 'SelfConsumptionRewardHistory',
      };

      cache.writeData({
        data: { selfConsumptionRewardHistory },
        variables: props,
      });

      return selfConsumptionRewardHistory;
    },
    citizenReferralRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?CitizenReferralRewardHistoryResponse> => {
      const columnsMapping = {
        DateUtc: 'date',
        ReferralComm: 'referralReward',
        Percentage: 'percentage',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          referralReward: ['ReferralCommFrom', 'ReferralCommTo'],
          percentage: ['PercentageFrom', 'PercentageTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: { ReferralCommList, PageCount = 1, GrandTotalComm },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetCitizenReferralCommTrxList',
        { params },
      );

      const items: CitizenReferralRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            referralReward: parseFloat,
            percentage: parseFloat,
          }),
          R.assoc('__typename', 'CitizenReferralRewardHistoryItem'),
        ),
        ReferralCommList,
      );

      const citizenReferralRewardHistory = {
        data: items,
        pages: PageCount,
        grandTotal: parseFloat(GrandTotalComm),
        __typename: 'CitizenReferralRewardHistory',
      };

      cache.writeData({
        data: { citizenReferralRewardHistory },
        variables: props,
      });

      return citizenReferralRewardHistory;
    },
    merchantReferralRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?MerchantReferralRewardHistoryResponse> => {
      const columnsMapping = {
        DateUtc: 'date',
        MerchantReferralComm: 'merchantReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          merchantReward: [
            'MerchantReferralCommFrom',
            'MerchantReferralCommTo',
          ],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get merchant referral reward list
      const {
        data: { MerchantReferralCommList, PageCount = 1, GrandTotalComm },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetMerchantReferralCommTrxList',
        { params },
      );

      const items: MerchantReferralRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            merchantReward: parseFloat,
          }),
          R.assoc('__typename', 'MerchantReferralRewardHistoryItem'),
        ),
        MerchantReferralCommList,
      );

      const merchantReferralRewardHistory = {
        data: items,
        pages: PageCount,
        grandTotal: parseFloat(GrandTotalComm),
        __typename: 'MerchantReferralRewardHistory',
      };

      cache.writeData({
        data: { merchantReferralRewardHistory },
        variables: props,
      });

      return merchantReferralRewardHistory;
    },
    superOrderCommissionsHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?SuperOrderCommissionsHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        SuperOrderComm: 'totalReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          totalReward: ['SuperOrderCommFrom', 'SuperOrderCommTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get list
      const {
        data: { SuperOrderCommList, PageCount = 1, TotalSuperOrderComm },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetSuperOrderCommTrxList',
        { params },
      );

      const items: SuperOrderCommissionsHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({ totalReward: parseFloat }),
          R.assoc('__typename', 'SuperOrderCommissionsHistoryItem'),
        ),
        SuperOrderCommList,
      );

      const superOrderCommissionsHistory = {
        data: items,
        pages: PageCount,
        totalSuperOrderReward: parseFloat(TotalSuperOrderComm),
        __typename: 'SuperOrderCommissionsHistory',
      };

      cache.writeData({
        data: { superOrderCommissionsHistory },
        variables: props,
      });

      return superOrderCommissionsHistory;
    },
    megopolyReward: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?MegopolyRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        MegopolyAmount: 'megopolyAmount',
        TrxId: 'trxId',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          megopolyAmount: ['MegopolyAmountFrom', 'MegopolyAmountTo'],
          trxId: 'TrxId',
        },
        R.invertObj(columnsMapping),
      )({ ...props });

      // Get list
      const {
        data: { MegopolyCashOutList, PageCount = 1, TotalMegopolyCashOut },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetMegopolyCashOutTrxList',
        { params },
      );

      const items: MegopolyRewardItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({ megopolyAmount: parseFloat }),
          R.assoc('__typename', 'MegopolyRewardItem'),
        ),
        MegopolyCashOutList,
      );

      const megopolyReward = {
        data: items,
        pages: PageCount,
        totalMegopolyCashOut: parseFloat(TotalMegopolyCashOut),
        __typename: 'MegopolyRewardHistory',
      };

      cache.writeData({
        data: { megopolyReward },
        variables: props,
      });

      return megopolyReward;
    },
    megopolyOpenMarket: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?MegopolyOpenMarketHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        MegopolyAmount: 'megopolyAmount',
        TrxId: 'trxId',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          megopolyAmount: ['MegopolyAmountFrom', 'MegopolyAmountTo'],
          trxId: 'TrxId',
        },
        R.invertObj(columnsMapping),
      )({ ...props });

      // Get list
      const {
        data: { MegopolyOpenMarketList, PageCount = 1, TotalMegopolyAmount },
      } = await HttpService.client.get(
        '/MyWealth/Consumption/GetMegopolyOpenMarketTrxList',
        { params },
      );

      const items: MegopolyOpenMarketItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({ megopolyAmount: parseFloat }),
          R.assoc('__typename', 'MegopolyOpenMarketItem'),
        ),
        MegopolyOpenMarketList,
      );

      const megopolyOpenMarket = {
        data: items,
        pages: PageCount,
        totalMegopolyAmount: parseFloat(TotalMegopolyAmount),
        __typename: 'MegopolyRewardHistory',
      };

      cache.writeData({
        data: { megopolyOpenMarket },
        variables: props,
      });

      return megopolyOpenMarket;
    },
  },
};
